import useRequest from "@/lib/request/useRequest";
import { useEffect, useMemo, useState } from "react";
import { Select, Selection, Slider, SelectItem } from "@heroui/react";
import {
    addUpdateGeojsonLayers,
    addUpdateGeojsonSource,
    removeLayersIfExists,
    removeSourcesIfExists,
} from "@/lib/utils/map";
import useMap from "@/lib/map/useMap";
import {
    CircleLayerSpecification,
    ExpressionSpecification,
    LngLatBoundsLike,
} from "mapbox-gl";
import { useParams } from "@tanstack/react-router";
import dayjs from "dayjs";
import { SoilSample } from "@/types/soil";
import * as turf from "@turf/turf";
import { sortArrayByKey } from "@/lib/utils/sorting";
import { binData } from "@/lib/utils/chart";
import {
    BarChart,
    ResponsiveContainer,
    XAxis,
    YAxis,
    Tooltip,
    Bar,
} from "recharts";

const SOURCE = "shpfile";

const LAYER = {
    id: "shpfile",
    type: "circle",
    source: SOURCE,
    paint: {
        "circle-radius": 15,
        "circle-blur": 0.1,
        "circle-color": "red",
    },
    layout: {
        visibility: "visible",
    },
} as CircleLayerSpecification;

function SoilProperties() {
    const { useGetRequest } = useRequest();
    const { fieldId } = useParams({
        from: "/dashboard/fields/$fieldId/insights",
    });

    const soilPropertiesQuery = useGetRequest<SoilSample[]>({
        url: `fields/${fieldId}/soil_samples/`,
        params: {
            field: fieldId,
        },
    });

    const [selectedUpload, setSelectedUpload] = useState<number | null>(null);

    const selected = useMemo(() => {
        return soilPropertiesQuery.data?.find((x) => x.id == selectedUpload);
    }, [soilPropertiesQuery.data, selectedUpload]);

    const fieldGeoJson = useMemo(() => {
        if (!selected) return null;
        return selected.soil_samples;
    }, [selected]);

    const [propertySelection, setPropertySelection] = useState<Selection>(
        new Set(),
    );

    const selectedProperty = useMemo(() => {
        if (propertySelection === "all") return null;
        return propertySelection.values().next().value ?? null;
    }, [propertySelection]);

    const properties = useMemo(() => {
        return Object.keys(
            fieldGeoJson?.features[0]?.properties?.soil_properties ?? {},
        );
    }, [fieldGeoJson]);

    const [min, setMin] = useState<number | null>(null);
    const [max, setMax] = useState<number | null>(null);

    const [minValue, maxValue] = useMemo(() => {
        let minValue = null;
        let maxValue = null;
        if (fieldGeoJson && selectedProperty) {
            // Collect all numeric values
            const values: number[] = [];
            for (const feature of fieldGeoJson.features) {
                const value =
                    feature.properties?.soil_properties?.[selectedProperty];
                if (typeof value === "number" && !isNaN(value)) {
                    values.push(value);
                }
            }
            if (values.length > 0) {
                minValue = Math.min(...values);
                maxValue = Math.max(...values);
            }
        }
        return [minValue, maxValue];
    }, [fieldGeoJson, selectedProperty]);

    useEffect(() => {
        // Get the min/max of the selectedProperty from the fieldGeoJson, disregarding the bottom 5% and top 5% of values
        if (min === null || (max === null && minValue && maxValue)) {
            setMin(minValue);
            setMax(maxValue);
        }
    }, [fieldGeoJson, max, maxValue, min, minValue, selectedProperty]);

    useEffect(() => {
        if (selectedProperty) {
            setMin(minValue);
            setMax(maxValue);
        }
    }, [fieldGeoJson, maxValue, minValue, selectedProperty]);

    const { map, mapLoaded } = useMap();

    useEffect(() => {
        if (!map || !mapLoaded) return;
        if (fieldGeoJson && selectedProperty) {
            if (!map.getSource(SOURCE)) {
                try {
                    const bbox = turf.bbox(fieldGeoJson);
                    map.fitBounds(bbox as LngLatBoundsLike, {
                        padding: 40,
                        duration: 800,
                    });
                    // eslint-disable-next-line no-empty
                } catch {}
            }
            addUpdateGeojsonSource(map, SOURCE, {
                type: "geojson",
                data: fieldGeoJson,
            });

            const l = map.getLayer(LAYER.id);
            if (l) {
                map.setPaintProperty(LAYER.id, "circle-color", [
                    "interpolate",
                    ["linear"],
                    ["get", selectedProperty, ["get", "soil_properties"]],
                    min ?? 0,
                    "#ff0000", // red at min
                    max ?? 1,
                    "#00ff00", // green at max
                ]);
            } else {
                addUpdateGeojsonLayers(map, [
                    {
                        ...LAYER,
                        paint: {
                            ...LAYER.paint,
                            "circle-color": [
                                "interpolate",
                                ["linear"],
                                ["get", selectedProperty as string],
                                min ?? 0,
                                "#ff0000", // red at min
                                max ?? 1,
                                "#00ff00", // green at max
                            ] as ExpressionSpecification,
                        },
                    },
                ]);
            }
        } else {
            removeLayersIfExists(map, [LAYER.id]);
            removeSourcesIfExists(map, [SOURCE]);
        }
        return () => {
            if (!map) return;
            removeLayersIfExists(map, [LAYER.id]);
            removeSourcesIfExists(map, [SOURCE]);
        };
    }, [fieldGeoJson, map, mapLoaded, max, min, selectedProperty]);

    const chartData = useMemo(() => {
        if (!fieldGeoJson || !selectedProperty) return [];
        const values = fieldGeoJson?.features
            .map((f) => f.properties?.soil_properties?.[selectedProperty])
            .filter((v) => typeof v === "number");
        return binData(values, 10);
    }, [fieldGeoJson, selectedProperty]);

    // Calculate min, max, mean, median, stdev for chartData
    const chartStats = useMemo(() => {
        if (!fieldGeoJson || !selectedProperty) {
            return {
                min: undefined,
                max: undefined,
                mean: undefined,
                median: undefined,
                stdev: undefined,
            };
        }
        const values = fieldGeoJson?.features
            .map((f) => f.properties?.soil_properties?.[selectedProperty])
            .filter((v) => typeof v === "number") as number[];

        if (!values.length) {
            return {
                min: undefined,
                max: undefined,
                mean: undefined,
                median: undefined,
                stdev: undefined,
            };
        }

        const sorted = [...values].sort((a, b) => a - b);
        const min = Math.min(...values);
        const max = Math.max(...values);
        const mean = values.reduce((a, b) => a + b, 0) / values.length;
        let median;
        if (sorted.length % 2 === 0) {
            median =
                (sorted[sorted.length / 2 - 1] + sorted[sorted.length / 2]) / 2;
        } else {
            median = sorted[Math.floor(sorted.length / 2)];
        }
        const stdev = Math.sqrt(
            values.reduce((acc, v) => acc + Math.pow(v - mean, 2), 0) /
                values.length,
        );

        return {
            min,
            max,
            mean,
            median,
            stdev,
        };
    }, [fieldGeoJson, selectedProperty]);

    return (
        <div className=" mb-48">
            <div className="flex flex-col gap-2">
                <div className="flex flex-row gap-2">
                    <Select
                        label="Soil Sample"
                        selectedKeys={
                            selectedUpload !== null
                                ? new Set([selectedUpload?.toString()])
                                : new Set()
                        }
                        onSelectionChange={(keys) => {
                            const key = Array.from(keys)[0];
                            setSelectedUpload(
                                key !== undefined ? Number(key) : null,
                            );
                        }}
                        selectionMode="single"
                        isDisabled={
                            !soilPropertiesQuery.data ||
                            soilPropertiesQuery.data.length === 0
                        }
                        labelPlacement="outside"
                        size="sm"
                        placeholder=" "
                    >
                        {sortArrayByKey(
                            soilPropertiesQuery.data ?? [],
                            "sample_date",
                            "desc",
                        ).map((sample) => (
                            <SelectItem
                                key={sample.id}
                                textValue={`Sample ${dayjs(
                                    sample.sample_date,
                                ).format("YYYY-MM-DD")}`}
                            >
                                Sample{" "}
                                {dayjs(sample.sample_date).format("YYYY-MM-DD")}
                            </SelectItem>
                        ))}
                    </Select>
                    <Select
                        selectedKeys={propertySelection}
                        onSelectionChange={setPropertySelection}
                        selectionMode="single"
                        isDisabled={!fieldGeoJson}
                        label="Property"
                        labelPlacement="outside"
                        size="sm"
                        placeholder=" "
                    >
                        {properties.map((property) => (
                            <SelectItem key={property}>{property}</SelectItem>
                        ))}
                    </Select>
                </div>
                <Slider
                    formatOptions={{
                        style: "decimal",
                    }}
                    step={0.01}
                    minValue={minValue ?? 0}
                    maxValue={maxValue ?? 1}
                    label="Property Range"
                    value={[min ?? 0, max ?? 1]}
                    onChange={(e: number[] | number) => {
                        if (Array.isArray(e)) {
                            setMin(e[0]);
                            setMax(e[1]);
                        } else {
                            setMin(e);
                            setMax(e);
                        }
                    }}
                />
                {/* <Slider
                    minValue={1}
                    maxValue={30}
                    label="Circle Size"
                    value={circleSize}
                    onChange={(e: number[] | number) => {
                        if (Array.isArray(e)) {
                            setCircleSize(e[0]);
                        } else {
                            setCircleSize(e);
                        }
                    }}
                /> */}
            </div>
            {chartData.length > 0 && (
                <>
                    <div className="flex flex-col gap-2">
                        <div className="font-semibold ">
                            Histogram - {selectedProperty}
                        </div>

                        <ResponsiveContainer width="100%" height={300}>
                            <BarChart
                                data={chartData}
                                margin={{
                                    bottom: 40,
                                }}
                            >
                                <XAxis
                                    dataKey="bin"
                                    angle={-20}
                                    textAnchor="end"
                                    fontSize={12}
                                />
                                <YAxis />
                                <Tooltip
                                    labelFormatter={(label) => {
                                        return `Between ${label}`;
                                    }}
                                    formatter={(value) => {
                                        return [`${value} samples`];
                                    }}
                                />
                                <Bar dataKey="count" fill="#09426A" />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                    {/* Simple stats table */}
                    <div className="mt-4">
                        <div className="font-semibold mb-2">
                            Summary Statistics
                        </div>
                        <div className="min-w-[300px] text-sm border border-gray-200 rounded grid grid-cols-2">
                            <div className="py-1 px-2 font-medium border-b border-gray-200">
                                Min
                            </div>
                            <div className="py-1 px-2 text-right border-b border-gray-200">
                                {chartStats.min !== undefined
                                    ? chartStats.min.toFixed(2)
                                    : "-"}
                            </div>
                            <div className="py-1 px-2 font-medium border-b border-gray-200">
                                Max
                            </div>
                            <div className="py-1 px-2 text-right border-b border-gray-200">
                                {chartStats.max !== undefined
                                    ? chartStats.max.toFixed(2)
                                    : "-"}
                            </div>
                            <div className="py-1 px-2 font-medium border-b border-gray-200">
                                Mean
                            </div>
                            <div className="py-1 px-2 text-right border-b border-gray-200">
                                {chartStats.mean !== undefined
                                    ? chartStats.mean.toFixed(2)
                                    : "-"}
                            </div>
                            <div className="py-1 px-2 font-medium border-b border-gray-200">
                                Median
                            </div>
                            <div className="py-1 px-2 text-right border-b border-gray-200">
                                {chartStats.median !== undefined
                                    ? chartStats.median.toFixed(2)
                                    : "-"}
                            </div>
                            <div className="py-1 px-2 font-medium">Std Dev</div>
                            <div className="py-1 px-2 text-right">
                                {chartStats.stdev !== undefined
                                    ? chartStats.stdev.toFixed(2)
                                    : "-"}
                            </div>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
}

export default SoilProperties;
